import { Injectable, NgZone } from "@angular/core";
import { Params, Router } from "@angular/router";
import { AuthUser, AuthUserHelper } from "@app/auth/model/auth-user";
import { AuthUserDbService, TS_AUTH_OPERATIONS, TS_ENTITIES } from "@app/auth/services/auth-user-db.service";
import { RootStoreActions, RouteInfo, SnackbarActions } from "@app/root-store/actions";
import { getQueryParams, getUrl } from "@app/root-store/selectors/root-store.selectors";
import { closeWebView, isEmbedded } from "@app/shared/resources/embedded-mobile-checker";
import { Actions, OnInitEffects, createEffect, ofType } from "@ngrx/effects";
import { Action, Store, select } from "@ngrx/store";
import { Observable, Subject, combineLatest, from, of, pipe, take } from "rxjs";
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, takeWhile, tap, withLatestFrom } from "rxjs/operators";

import { AngularFirestore } from "@angular/fire/compat/firestore";
import { MatDialog } from "@angular/material/dialog";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { LogoutConfirmationDialogComponent } from "@app/auth/components/logout-confirmation/logout-confirmation-dialog/logout-confirmation-dialog.component";
import { FirebaseAuthService } from "@app/auth/services/firebase-auth.service";
import { AuthState } from "@app/auth/store/auth.state";
import { EntityDbService } from "@app/entity/services/entity-db.service";
import { EntityActions } from "@app/entity/store/actions";
import { ProjectActions } from "@app/project/store";
import { ProjectFeatureActions } from "@app/project/store/actions";
import { RouterActions } from "@app/root-store";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { ErrorToSuppress } from "@app/shared/services/db-crud-helper";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { SessionStorageService } from "@app/shared/services/storage/session-storage.service";
import { ROLE_NAMES } from "@app/user/model/user-role";
import { UserManagementService } from "@app/user/service/user-management.service";
import { User as FirebaseUser } from "firebase/auth";
import { NgxPermissionsService } from "ngx-permissions";
import { AuthenticatedWSUser } from "../../../user/model/user";
import { AuthActions } from "../actions";
import { LoginRedirectObj } from "../actions/auth.actions";
import { AuthSelectors } from "../selectors";

// prettier-ignore

export enum AuthEffectErrors {
    invalidCredentials = "Invalid username or password",
    tooManyAuthAttempts = "The account has been temporarily disabled. Please wait or reset the password.",
    invalidInviteCode = "This invite link is no longer valid",
    invalidUser = "This user has not signed up. Please use an invite link to complete the sign up process.",
    invalidSession = "Invalid session. Please sign in again.",
    userNotFound = "The user was not found. It may be that the user did not complete the sign up process.",
    emailNotFound = "The email address does not exist",
    oobCodeNotFound = "The action code is invalid. This can happen if the code is malformed, expired, or has already been used.",
    invalidInviteLinkCustomer = "The invite link is no longer valid. Please request a new invite link from the primary customer or your dealer.",
    invalidInviteLinkStaff = "The invite link is no longer valid. Please request a new invite link.",
}

export enum AuthErrorCode {
    invalidCredentials = "auth/wrong-password",
    tooManyAuthAttempts = "auth/too-many-requests",
    tokenNotValid = "auth/expired-action-code",
}

@Injectable()
export class AuthEffects implements OnInitEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly firebaseAuth: FirebaseAuthService,
        private readonly permissionsService: NgxPermissionsService,
        private readonly authUserDbService: AuthUserDbService,
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly store: Store<AuthState>,

        private readonly sessionStorageService: SessionStorageService,
        private readonly userManegement: UserManagementService,
        private readonly ngZone: NgZone,
        private readonly entityDbService: EntityDbService,
        private readonly auditLogService: AuditLogService,
        private readonly firestore?: AngularFirestore
    ) {}
    private readonly firebaseUserNotFoundString = "user not found";

    ngrxOnInitEffects() {
        return AuthActions.watchLogin();
    }

    watchLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.watchLogin),
                // Need to wait until the url gets the query parameters
                switchMap(_ =>
                    this.store.pipe(select(getUrl)).pipe(
                        takeWhile(url => !url, true),
                        filter(url => !!url)
                    )
                ),
                tap(_ => this.authUserDbService.subscribeToWebsocketLogoutObserver()),
                switchMap(_ => {
                    const userSub = new Subject();
                    this.firebaseAuth.onAuthStateChanged(userSub);
                    return userSub.pipe(this.processLoginAndReturnParamsPipe());
                }),
                tap(() => this.firebaseAuth.setBrowserSessionPersistence())
            ),
        { dispatch: false }
    );

    processLoginAndReturnParamsPipe() {
        return pipe(
            withLatestFrom(
                this.store.pipe(select(AuthSelectors.selectGetUser)),
                this.store.pipe(select(AuthSelectors.selectGetPending)),
                this.store.pipe(select(getQueryParams))
            ),
            switchMap(([user, authUser, isPending, params]: [FirebaseUser, AuthUser, boolean, Params]) => {
                if (user && !authUser && isPending) {
                    return from(user.getIdToken(true).then((token: string) => [token, params?.redirectTo, params?.inviteCode, user]));
                }

                return of([params?.fbtkn, params?.redirectTo, params?.inviteCode, user]);
            }),
            withLatestFrom(this.store.pipe(select(getUrl))),
            map(([[token, redirect, inviteCode, user], url]) => {
                let loginToken = token;

                // Embedded mode only
                if (isEmbedded()) {
                    // If a token exists is in LocalStorage
                    if (!token && window.localStorage.getItem("fbtkn")) {
                        loginToken = window.localStorage.getItem("fbtkn");
                    } else if (token) {
                        // Else a token exists in the current route
                        window.localStorage.setItem("fbtkn", token);
                    }
                }
                if (loginToken) {
                    return [loginToken, redirect, inviteCode, user];
                }

                if (!this.authUserDbService.isLoginOrSignUpUrl(url)) {
                    this.ngZone.run(() => {
                        this.store.dispatch(AuthActions.logOut({ redirectTo: url }));
                    });
                }
                return [];
            }),
            filter(([token]) => !!token),
            tap(([token, redirect, inviteCode, user]) => {
                this.ngZone.run(() => {
                    this.store.dispatch(
                        AuthActions.processLogin({
                            token,
                            redirect,
                            inviteCode,
                            user,
                        })
                    );
                });
            })
        );
    }

    throwErrorIfInvalidUser(fbUser: any) {
        if (!fbUser) {
            throw Error(AuthEffectErrors.invalidSession);
        }
        if (typeof fbUser === "object" && fbUser.error) {
            let error = fbUser.error;
            if (error === this.firebaseUserNotFoundString) {
                error = AuthEffectErrors.invalidUser;
            }

            throw Error(error);
        }
    }

    getLoginFailureAction$(error: any, inviteCode: string): Observable<Action> {
        if (error && inviteCode) {
            let message = `Your sign in information was saved, however we were unable to verify your invite code. 
                You will not be able to sign in until this is complete. Please contact support to resolve this issue.`;

            if (error.name !== "FailedSession") {
                message = this.authUserDbService.parseFirebaseErrorMessage(error);
            }

            return of(AuthActions.registerFailure({ message }));
        }

        let errMessage: string;

        if (typeof error === "string") {
            errMessage = this.authUserDbService.parseFirebaseErrorMessage(error);
        } else {
            errMessage = error.message || error.error;
        }
        console.log(error);
        return of(AuthActions.logInFailure({ message: errMessage }));
    }

    private getUserInfo(user: AuthenticatedWSUser) {
        return combineLatest({
            ssoUserInfo: this.authUserDbService.getUserInfo(user),
            roles: this.userManegement.dispatchAndSelectAllUserRoles().pipe(take(1)),
        }).pipe(
            exhaustMap(({ ssoUserInfo, roles }) => {
                const userRoles = roles.find(r => r.id === ssoUserInfo.role_id);
                ssoUserInfo.role_name = {
                    name: userRoles.name,
                };

                const info = {
                    Email: user.email || "",
                    UID: user.rawId || "",
                    DisplayName: user.displayName || "",
                    ssoUserInfo,
                    ssoUserPermissions: userRoles.permissions || null,
                };

                return this.entityDbService.get(ssoUserInfo.entity_id, [ErrorToSuppress.PERMISSION_DENIED_ERROR]).pipe(
                    map(entity => ({
                        ...info,
                        entityName: entity.name,
                    })),
                    catchError((error: DataServiceError<{ error: string }>) => {
                        // [DDB-5349]: if customer log in, permission error might happen
                        //  We ignore it for now
                        if (error.error === ErrorToSuppress.PERMISSION_DENIED_ERROR) {
                            return of({
                                ...info,
                                entityName: "",
                            });
                        }

                        throw new DataServiceError(error.error);
                    })
                );
            })
        );
    }

    processLogin: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.processLogin),
            switchMap(({ token, inviteCode, redirect, user }) => {
                if (!token) {
                    console.log("Token is missing");
                }

                return this.authUserDbService.setSocketToken(token, inviteCode).pipe(
                    tap(fbUser => this.throwErrorIfInvalidUser(fbUser)),
                    switchMap(fbUser => this.getUserInfo(fbUser)),
                    // switchMap(fbUser => this.authUserDbService.getUserInfo(fbUser)),

                    tap(user => {
                        if (!user) {
                            throw Error(AuthEffectErrors.userNotFound);
                        }
                    }),
                    map(userData => AuthUserHelper.fromDbObject(userData)),
                    map(userData => {
                        if (userData.role.name === ROLE_NAMES.CUSTOMER) {
                            // Backend uses the viewProject permission to grant API access to Project table in DB,
                            // but UI should not allow access to project pages
                            userData.permissions = userData.permissions.filter(p => p !== "viewProject");
                        }
                        return userData;
                    }),
                    tap(authUser => {
                        if (user && authUser?.role?.name !== ROLE_NAMES.CUSTOMER) {
                            const lastLoginTime = +user?.metadata["lastLoginAt"];
                            const currentDate = new Date().getTime();
                            const diffInLoginTimeAndCurrentTime = currentDate - lastLoginTime;

                            user.getIdTokenResult()
                                .then(idTokenResult => {
                                    const signInProvider = idTokenResult.signInProvider;
                                    console.log("Sign-in provider:", signInProvider);
                                    if (diffInLoginTimeAndCurrentTime < 2000) {
                                        const docRef = this.firestore.collection("User Login").doc(user.email);
                                        const logKey = createAuditLogKey(LOG_CATEGORY.SYSTEM, LOG_TYPE.USER, LOG_SUB_TYPE.LOG_IN);
                                        this.auditLogService.logEvent(logKey, {
                                            entityId: authUser.entityId,
                                            data: `Logged In`,
                                        });
                                        return docRef
                                            .get()
                                            .toPromise()
                                            .then(doc => {
                                                const data = doc.data() as { [key: string]: boolean };
                                                if (doc.exists) {
                                                    console.log("user doc exists, update it now");
                                                    const newData = { ...data, [signInProvider.split(".")[0]]: true };
                                                    docRef.update(newData).catch(error => console.log(error));
                                                } else {
                                                    console.log("user doc doesn't exist, adding now");
                                                    docRef
                                                        .set({ email: user.email, [signInProvider.split(".")[0]]: true })
                                                        .catch(error => console.log(error));
                                                }
                                            });
                                    }
                                })
                                .catch(error => console.error("Error getting ID token result:", error));
                        }
                        if (authUser.permissions?.length) {
                            this.permissionsService.loadPermissions(authUser.permissions);
                        }
                    }),

                    map((authUser: AuthUser) => {
                        this.authUserDbService.isPendingLoginState = false;
                        return AuthActions.logInSuccess({ authUser, redirect });
                    }),
                    catchError(error => {
                        this.authUserDbService.isPendingLoginState = false;
                        this.authUserDbService.logEvent(TS_ENTITIES.SSO_DB, TS_AUTH_OPERATIONS.FB_SIGN_IN_ERROR, {
                            inviteCode,
                            token,
                            error,
                        });

                        // DDB-5181: close web view if the token was expired
                        if (isEmbedded()) {
                            closeWebView();
                        }

                        return this.getLoginFailureAction$(error, inviteCode);
                    })
                );
            }),
            catchError(error => {
                this.authUserDbService.logEvent(TS_ENTITIES.SSO_DB, TS_AUTH_OPERATIONS.FB_SIGN_IN_ACTION_ERROR, { error });
                console.error(error);
                return of(AuthActions.logOut(null));
            })
        )
    );

    logout: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.logOut, AuthActions.logInFailure),
            exhaustMap(payload => {
                this.authUserDbService.isPendingLoginState = false;
                this.permissionsService.flushPermissions();
                this.dialog.closeAll();
                return from(this.firebaseAuth.signOut()).pipe(
                    exhaustMap(() => {
                        let logoutAction = SnackbarActions.emptyAction();
                        let snackbarAction = SnackbarActions.emptyAction();

                        if (
                            !this.authUserDbService.isLoginOrSignUpUrl(this.router.url) ||
                            this.router.url.startsWith(`${this.authUserDbService.loginPath}/social/`) ||
                            payload.type === AuthActions.logOut.type
                        ) {
                            logoutAction = AuthActions.logInRedirect(payload);
                        }

                        if (payload.message) {
                            snackbarAction = SnackbarActions.openSnackbar({
                                message: new HttpResponseHandler(null, null, payload.message, true),
                            });
                        }

                        let cachePageSize;
                        if (window.localStorage?.getItem("cachePageSize")) {
                            cachePageSize = +window.localStorage.getItem("cachePageSize");
                        }
                        let signoutWhileProcessingWizard;
                        if (window.localStorage?.getItem("processSignOut")) {
                            signoutWhileProcessingWizard = window.localStorage.getItem("processSignOut");
                        }

                        const selectedEntity = window.localStorage?.getItem("selectedEntity");

                        window.localStorage.clear();
                        if (cachePageSize) {
                            window.localStorage.setItem("cachePageSize", cachePageSize);
                        }
                        if (signoutWhileProcessingWizard) {
                            window.localStorage.setItem("processSignOut", signoutWhileProcessingWizard);
                        }
                        if (selectedEntity) {
                            window.localStorage.setItem("selectedEntity", selectedEntity);
                        }

                        this.sessionStorageService.clearAll();
                        if (payload.type === AuthActions.logOut.type) {
                            // Clearing the root store state on login failure clears the auth error,
                            // so only do it on logOut
                            return [logoutAction, snackbarAction, RootStoreActions.clearRootState()];
                        }
                        return [logoutAction, snackbarAction];
                    })
                );
            }),
            catchError(e => {
                console.error(e);
                return of(e);
            })
        )
    );

    logoutConfirmation = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.logOutConfirmation),
            exhaustMap(() => this.dialog.open(LogoutConfirmationDialogComponent).afterClosed()),
            map(result => {
                if (result) {
                    this.authUserDbService.triggerSignOut();
                    return SnackbarActions.emptyAction();
                }
                return AuthActions.logOutConfirmationDismiss();
            })
        )
    );

    loginSuccess: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.logInSuccess),
            map(({ redirect }) => {
                this.clearAllOutdatedData();
                let newAction: Action = AuthActions.clearError();
                this.loadAdditionalFeatures();

                if (redirect || this.authUserDbService.isLoginOrSignUpUrl(this.router.url)) {
                    const route = this.authUserDbService.getLoginRedirectUrl(redirect, this.authUserDbService.isSignUpUrl(this.router.url));
                    this.store.dispatch(ProjectActions.setProjectListEntity());
                    newAction = RouterActions.routerGo(route);
                }
                return newAction;
            }),
            catchError(_ => of(AuthActions.clearError()))
        )
    );

    registerFailure = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.registerFailure),
            tap(_ => (this.authUserDbService.isPendingLoginState = false)),
            mergeMap(({ message, authUser }) => {
                const actions: Action[] = [
                    SnackbarActions.openSnackbar({
                        message: new HttpResponseHandler(null, null, message, true),
                    }),
                ];
                if (message !== "The email address is already in use by another account." && !message.includes("email-already-in-use")) {
                    actions.push(RouterActions.routerGo({ path: ["/signup"], extras: { queryParamsHandling: "merge" } }));
                } else {
                    this.authUserDbService.logEvent("ssopgrest", "add", { user: authUser });
                }
                return actions;
            })
        )
    );

    loginRedirect = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.logInRedirect),
            mergeMap(({ redirectTo, message, code }: LoginRedirectObj) => {
                let params;
                // if the current page is a login page and the param already contains a redirect param
                if ((this.router.url === "/" || this.router.url.startsWith(this.authUserDbService.loginPath)) && redirectTo) {
                    // use the current redirect in the url
                    params = redirectTo;

                    // if the current url is not a login page, redirect to the current url
                } else if (!this.router.url.startsWith(this.authUserDbService.loginPath)) {
                    params = this.router.url;
                }

                let snackbarAction = SnackbarActions.emptyAction();

                if (message) {
                    snackbarAction = SnackbarActions.openSnackbar({
                        message: new HttpResponseHandler(null, null, message, true),
                    });
                }

                const actions = [snackbarAction];

                // If the error code from firebase is not set or does not include "auth/", redirect to the login
                if (!code?.includes("auth/") && !params?.includes("login?redirectTo=")) {
                    const payload: RouteInfo = {
                        path: [this.authUserDbService.loginPath],
                    };
                    if (params !== undefined) {
                        payload.query = { redirectTo: params };
                    }

                    actions.push(RouterActions.routerGo(payload));
                }
                return actions;
            })
        )
    );

    register = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.register),
            switchMap(auth =>
                this.authUserDbService.registerFirebaseUser$(auth).pipe(
                    map(() => SnackbarActions.emptyAction()),
                    catchError((error: Error) =>
                        of(
                            AuthActions.registerFailure({
                                message: error?.message,
                                authUser: {
                                    email: auth.email,
                                    password: auth.password,
                                    displayName: auth.displayName,
                                    inviteCode: auth.inviteCode,
                                },
                            })
                        )
                    )
                )
            )
        )
    );

    resetPassword = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.resetPassword),
            switchMap(({ email }) =>
                from(this.firebaseAuth.fetchSignInMethodsForEmail(email)).pipe(
                    map(emailProvidersForUser => ({ emailProvidersForUser, email }))
                )
            ),
            switchMap(({ emailProvidersForUser, email }) => {
                if (emailProvidersForUser.length > 0) {
                    return from(this.firebaseAuth.sendPasswordResetEmail(email));
                }
                return of(null);
            }),
            map(() => AuthActions.resetPasswordSuccess(null)),
            catchError(error => of(AuthActions.resetPasswordFailure({ message: error.message })))
        )
    );

    changePassword = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.changePassword),
            switchMap(({ oobCode, newPassword, continueUrl }) =>
                from(this.firebaseAuth.confirmPasswordReset(oobCode, newPassword, continueUrl)).pipe(
                    map(() => AuthActions.changePasswordSuccess(null))
                )
            ),
            catchError(error => of(AuthActions.resetPasswordFailure({ message: error.message })))
        )
    );

    resetPasswordSuccess = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.resetPasswordSuccess),
            withLatestFrom(this.store.pipe(select(AuthSelectors.selectGetLoggedIn))),
            map(([_, loggedIn]) => {
                const redirectPath = loggedIn ? "/dashboard" : `${this.authUserDbService.loginPath}/reset-password-sent`;
                return RouterActions.routerGo({ path: [redirectPath] });
            })
        )
    );

    updatePasswordSuccess = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.changePasswordSuccess),
            map(() =>
                SnackbarActions.openSnackbar({
                    message: new HttpResponseHandler(null, null, "Password has been updated!"),
                    buttonText: null,
                    duration: 3000,
                })
            )
        )
    );

    verifyResetPasswordOobCode = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.verifyPassword),
            switchMap(({ oobCode }) =>
                from(this.firebaseAuth.verifyPasswordResetCode(oobCode)).pipe(map(() => AuthActions.verifyPasswordSuccess(null)))
            ),
            catchError((error: { message: string; code: string }) => {
                const firebaseInvalidCode = "auth/invalid-action-code";

                const message =
                    error.code === firebaseInvalidCode ? "The token is invalid, the request is expired or had been used." : error.message;
                return of(AuthActions.verifyPasswordFailure({ message, errorCode: error.code }));
            })
        )
    );

    verifyPasswordFailed = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.verifyPasswordFailure, AuthActions.resetPasswordFailure, AuthActions.validateInviteCodeFailure),
            map(({ message }) =>
                SnackbarActions.openSnackbar({
                    message: new HttpResponseHandler(null, null, message, true),
                })
            )
        )
    );

    validateInviteCode = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.validateInviteCode),
            switchMap(({ inviteCode, isStaff }) =>
                this.authUserDbService.validateInviteCode({ inviteCode }).pipe(
                    map(result => {
                        const isValid = result["invite_code_existed"] === true;
                        return AuthActions.validateInviteCodeSuccess({
                            isInviteCodeValid: isValid,
                            message: !isValid
                                ? isStaff
                                    ? AuthEffectErrors.invalidInviteLinkStaff
                                    : AuthEffectErrors.invalidInviteLinkCustomer
                                : null,
                        });
                    })
                )
            ),

            catchError(error => of(AuthActions.validateInviteCodeFailure({ message: error.message })))
        )
    );

    clearError = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.clearError),
                tap(_ => (this.authUserDbService.isPendingLoginState = false))
            ),
        { dispatch: false }
    );

    /**
     * Clear all remaining data from previous logged in session
     */
    private clearAllOutdatedData() {
        this.store.dispatch(EntityActions.clearEntityState());
    }

    /**
     * load metadata
     */
    private loadAdditionalFeatures() {
        this.store.dispatch(ProjectFeatureActions.loadAllAdditionalFeatures.request(null));
        this.auditLogService.dispatchGetLogTypes();
    }
}
